import type {ConnectionOperation, EntityOperation} from '@github-ui/optimistic-connection'
import {
  createConnectionMutationId,
  dispatchConnectionMutation,
  publishOptimisticFields,
} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, graphql} from 'react-relay'
import type {Environment, RecordSourceProxy} from 'relay-runtime'
import type {
  RemoveSubIssueInput,
  removeSubIssueMutation,
  removeSubIssueMutation$data,
} from './__generated__/removeSubIssueMutation.graphql'
import {isSubIssuesOptimisticOverlayEnabled} from '../utils/optimistic-overlay'

const SUB_ISSUES_CONNECTION_PATH = ['subIssues']

function getSubIssuesConnection(parentRecord: NonNullable<ReturnType<RecordSourceProxy['get']>>) {
  return parentRecord.getLinkedRecord('subIssues', {first: 100}) || null
}

function getSubIssuesConnectionAlias(parentRecord: NonNullable<ReturnType<RecordSourceProxy['get']>>) {
  return parentRecord.getLinkedRecord('subIssuesConnection') || null
}

function optimisticRemoveSubIssueFromConnection(store: RecordSourceProxy, input: RemoveSubIssueInput) {
  const subIssueRecord = store.get(input.subIssueId)

  const parentRecord = store.get(input.issueId)
  if (parentRecord) {
    const subIssuesConnection = getSubIssuesConnection(parentRecord)

    if (subIssuesConnection) {
      const currentNodes = subIssuesConnection.getLinkedRecords('nodes') || []
      const newNodes = currentNodes.filter(node => node?.getValue('id') !== input.subIssueId)

      if (newNodes.length !== currentNodes.length) {
        subIssuesConnection.setLinkedRecords(newNodes, 'nodes')

        const currentTotalCount = subIssuesConnection.getValue('totalCount')
        if (typeof currentTotalCount === 'number') {
          subIssuesConnection.setValue(Math.max(0, currentTotalCount - 1), 'totalCount')
        }
      }

      // Update subIssuesSummary on the parent if it exists
      const subIssuesSummary = parentRecord.getLinkedRecord('subIssuesSummary')
      if (subIssuesSummary) {
        const currentTotal = subIssuesSummary.getValue('total')
        if (typeof currentTotal === 'number') {
          subIssuesSummary.setValue(Math.max(0, currentTotal - 1), 'total')
        }

        // Update completed count if the sub-issue is closed
        if (subIssueRecord) {
          const isClosed = subIssueRecord.getValue('closed') === true
          if (isClosed) {
            const currentCompleted = subIssuesSummary.getValue('completed')
            if (typeof currentCompleted === 'number') {
              subIssuesSummary.setValue(Math.max(0, currentCompleted - 1), 'completed')
            }
          }
        }
      }

      // Update subIssuesConnection totalCount (aliased field used by useSubIssuesSummary)
      const subIssuesConnectionRecord = getSubIssuesConnectionAlias(parentRecord)
      if (subIssuesConnectionRecord) {
        const connectionTotalCount = subIssuesConnectionRecord.getValue('totalCount')
        if (typeof connectionTotalCount === 'number') {
          subIssuesConnectionRecord.setValue(Math.max(0, connectionTotalCount - 1), 'totalCount')
        }
      }
    }
  }

  // Always clear the parent field on the sub-issue so the RelationshipsSection updates immediately
  if (subIssueRecord) {
    subIssueRecord.setValue(null, 'parent')
  }
}

export function commitRemoveSubIssueMutation({
  environment,
  input,
  onError,
  onCompleted,
  subIssueClosed,
}: {
  environment: Environment
  input: RemoveSubIssueInput
  onError?: (error: Error) => void
  onCompleted?: (response: removeSubIssueMutation$data) => void
  // Caller-supplied closed state for the overlay's `completed` delta: the overlay renders
  // from the TanStack view, where the sub-issue may not be in the Relay store.
  subIssueClosed?: boolean
}) {
  // Mirror the Relay mutation into the secondary view overlay.
  const overlayEnabled = isSubIssuesOptimisticOverlayEnabled()
  const overlayMutationId = overlayEnabled ? createConnectionMutationId() : ''
  // Read before commitMutation starts so every optimistic publication precedes a possible
  // synchronous terminal. The caller supplies this when the child only exists in the TSQ overlay.
  const storedSubIssue = environment.getStore().getSource().get(input.subIssueId)
  let removedSubIssueClosed = storedSubIssue?.closed === true || storedSubIssue?.state === 'CLOSED'

  // The operation mirrored into the TanStack overlay; prefer the caller's closed state.
  const buildRemoveOperation = (): ConnectionOperation => {
    const closed = subIssueClosed ?? removedSubIssueClosed
    return {
      type: 'removeFromConnection',
      connectionPath: SUB_ISSUES_CONNECTION_PATH,
      nodeId: input.subIssueId,
      counts: [
        {path: ['subIssuesSummary', 'total'], delta: -1},
        {path: ['subIssuesConnection', 'totalCount'], delta: -1},
        ...(closed ? [{path: ['subIssuesSummary', 'completed'], delta: -1}] : []),
      ],
    }
  }
  const buildSummaryOperation = (): EntityOperation => {
    const closed = subIssueClosed ?? removedSubIssueClosed
    return {
      type: 'deltaEntityFieldValues',
      id: input.issueId,
      field: 'subIssuesSummary',
      deltas: {total: -1, ...(closed ? {completed: -1} : {})},
    }
  }
  const buildConfirmedSummaryOperation = (summary: {total: number; completed: number}): EntityOperation => ({
    type: 'setFields',
    id: input.issueId,
    fields: {subIssuesSummary: summary},
  })
  const summaryPublication = publishOptimisticFields(buildSummaryOperation())

  let disposable
  try {
    disposable = commitMutation<removeSubIssueMutation>(environment, {
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      mutation: graphql`
        mutation removeSubIssueMutation($input: RemoveSubIssueInput!) @raw_response_type {
          removeSubIssue(input: $input) {
            errors {
              message
            }
            issue {
              ... on Issue {
                id
                state
                stateReason
                issueDependenciesSummary {
                  blockedBy
                }
                assignees(first: 10) {
                  totalCount
                  edges {
                    node {
                      id
                      login
                      avatarUrl
                    }
                  }
                }
                url
                repository {
                  name
                  owner {
                    login
                  }
                }
                databaseId
                number
                title
                titleHTML
                issueType {
                  id
                  name
                  color
                }
                subIssuesSummary {
                  total
                  completed
                }
                closedByPullRequestsReferences(first: 0, includeClosedPrs: true) {
                  totalCount
                }
                subIssues(first: 100) {
                  nodes {
                    id
                  }
                }
              }
            }
            subIssue {
              id
              parent {
                id
              }
            }
          }
        }
      `,
      variables: {
        input,
      },
      optimisticUpdater: store => {
        // Some callers select `state` but not `closed`; accept either so removing a closed
        // child still decrements `subIssuesSummary.completed`.
        const removed = store.get(input.subIssueId)
        removedSubIssueClosed = removed?.getValue('closed') === true || removed?.getValue('state') === 'CLOSED'
        optimisticRemoveSubIssueFromConnection(store, input)
      },
      onError: error => {
        if (overlayEnabled) dispatchConnectionMutation({phase: 'error', mutationId: overlayMutationId, error})
        summaryPublication?.failed(error)
        onError?.(error)
      },
      onUnsubscribe: () => {
        // Relay rolls back on disposal here (not onError/onCompleted); end the bridged op too or
        // it stays pending. Harmless orphan error after a normal settle.
        if (overlayEnabled)
          dispatchConnectionMutation({
            phase: 'error',
            mutationId: overlayMutationId,
            error: new Error('Sub-issue removal was cancelled'),
          })
        summaryPublication?.failed(new Error('Sub-issue removal was cancelled'))
      },
      onCompleted: (response, errors) => {
        const payloadUserErrors = response.removeSubIssue?.errors?.length ?? 0
        const failed = (errors?.length ?? 0) > 0 || response.removeSubIssue == null || payloadUserErrors > 0
        if (overlayEnabled) {
          // Relay reports failures here, not onError: a GraphQL error (top-level `errors`, null
          // payload) or a user error (successful response, populated payload `errors`). Treat
          // either as an overlay `error` so the removal rolls back instead of folding into base.
          dispatchConnectionMutation(
            failed
              ? {
                  phase: 'error',
                  mutationId: overlayMutationId,
                  error: new Error(
                    response.removeSubIssue?.errors?.[0]?.message ??
                      errors?.[0]?.message ??
                      'Failed to remove sub-issue',
                  ),
                }
              : {
                  phase: 'completed',
                  mutationId: overlayMutationId,
                  ownerId: input.issueId,
                  connectionPath: SUB_ISSUES_CONNECTION_PATH,
                  operation: buildRemoveOperation(),
                },
          )
        }
        if (failed) {
          summaryPublication?.failed(
            new Error(
              response.removeSubIssue?.errors?.[0]?.message ?? errors?.[0]?.message ?? 'Failed to remove sub-issue',
            ),
          )
        } else if (summaryPublication) {
          const summary = response.removeSubIssue?.issue?.subIssuesSummary
          if (summary) summaryPublication.completed(buildConfirmedSummaryOperation(summary))
          else summaryPublication.failed(new Error('Sub-issue summary was missing from the response'))
        }
        onCompleted?.(response)
      },
    })
  } catch (error) {
    summaryPublication?.failed(error instanceof Error ? error : new Error('Failed to remove sub-issue'))
    throw error
  }

  // optimisticUpdater has run synchronously by now, so removedSubIssueClosed is set.
  if (overlayEnabled) {
    dispatchConnectionMutation({
      phase: 'optimistic',
      mutationId: overlayMutationId,
      ownerId: input.issueId,
      connectionPath: SUB_ISSUES_CONNECTION_PATH,
      operation: buildRemoveOperation(),
    })
  }

  return disposable
}
